import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  Check,
  MinusCircle,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { isTransactable, listAccounts } from '@/api/accounts'
import { createSettlement, type Contact, type OwedEntry } from '@/api/people'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { allocate, cancelOut, fullNet, type AllocatableEntry } from '@/lib/allocateOldestFirst'
import { formatShortDate } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'

type Mode = 'PAID' | 'WRITE_OFF' | 'EVEN'
type ExtraChoice = 'OTHER_ITEMS' | 'OWE_BACK' | 'ROUNDED_OFF'

const LABEL_CLS = 'mb-1.5 block text-[13px] font-medium text-[var(--fl-text-1)]'
const SECTION_CLS = 'text-xs font-semibold uppercase tracking-wider'

function toAllocatable(e: OwedEntry): AllocatableEntry {
  return {
    id: e.id,
    direction: e.direction,
    outstandingPaise: toPaise(e.outstanding),
    entryDate: e.entryDate,
  }
}

function sidePaise(entries: OwedEntry[], dir: OwedEntry['direction']) {
  return entries.filter((e) => e.direction === dir).reduce((s, e) => s + toPaise(e.outstanding), 0)
}

/** Settle up in two steps: what happened (paid / write off / even), then one short form for it. */
export function SettleUpModal({
  contact,
  entries,
  onClose,
}: {
  contact: Contact
  entries: OwedEntry[]
  onClose: () => void
}) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const titleId = useId()
  const name = contact.name
  const [mode, setMode] = useState<Mode | null>(null)
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(entries.map((e) => e.id)))
  const [choosingItems, setChoosingItems] = useState(false)
  // null = follow the full net as ticks change; a string = the user typed their own amount.
  const [amountText, setAmountText] = useState<string | null>(null)
  const [extraChoice, setExtraChoice] = useState<ExtraChoice | null>(null)
  const [accountId, setAccountId] = useState('')
  const [date, setDate] = useState(today())
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const accounts = (accountsData?.accounts ?? []).filter((a) => isTransactable(a.type))

  const owedAll = sidePaise(entries, 'OWED_TO_US')
  const oweAll = sidePaise(entries, 'WE_OWE')
  const overall = owedAll - oweAll
  const rupees = (paise: number) => format(fromPaise(paise))

  // --- Paid ---
  const tickedOpen = entries.filter((e) => ticked.has(e.id))
  const tickedEntries = tickedOpen.map(toAllocatable)
  const { netPaise, direction } = fullNet(tickedEntries)
  const trimmed = amountText?.trim()
  let paidPaise = netPaise
  let amountError: string | null = null
  if (amountText !== null) {
    const parsed = Number(trimmed)
    paidPaise = trimmed && !Number.isNaN(parsed) ? toPaise(parsed) : NaN
    if (Number.isNaN(paidPaise) || paidPaise <= 0) amountError = 'Enter an amount more than ₹0'
  }
  const extraPaise = !amountError && direction !== 'EVEN' ? Math.max(0, paidPaise - netPaise) : 0
  // Paying more can also clear the same side's unticked items, when the extra fits within them.
  const widened = [
    ...tickedEntries,
    ...entries
      .filter(
        (e) => !ticked.has(e.id) && e.direction === (direction === 'OUT' ? 'WE_OWE' : 'OWED_TO_US')
      )
      .map(toAllocatable),
  ]
  const canUseOtherItems =
    extraPaise > 0 &&
    widened.length > tickedEntries.length &&
    paidPaise <= fullNet(widened).netPaise
  const extraHandling =
    extraPaise === 0 || (extraChoice === 'OTHER_ITEMS' && !canUseOtherItems) ? null : extraChoice
  const paidAllocations =
    mode !== 'PAID' || direction === 'EVEN' || amountError || (extraPaise > 0 && !extraHandling)
      ? []
      : extraHandling === 'OTHER_ITEMS'
        ? allocate(widened, paidPaise)
        : allocate(tickedEntries, paidPaise - extraPaise)
  const payloadExtra =
    extraHandling === 'OWE_BACK' || extraHandling === 'ROUNDED_OFF'
      ? { amount: fromPaise(extraPaise), handling: extraHandling }
      : undefined

  // --- Even ---
  const even = cancelOut(entries.map(toAllocatable))

  const allocationsPaise =
    mode === 'PAID'
      ? paidAllocations
      : mode === 'EVEN'
        ? even.allocations
        : tickedEntries.map((e) => ({ entryId: e.id, amountPaise: e.outstandingPaise }))

  // What's still owed each way once this is saved.
  const clearedBy = (dir: OwedEntry['direction']) =>
    allocationsPaise
      .filter((a) => entries.find((e) => e.id === a.entryId)?.direction === dir)
      .reduce((s, a) => s + a.amountPaise, 0)
  // An overpayment owed back flips to the other side: they paid extra → you now owe it.
  const owedBack =
    mode === 'PAID' && extraHandling === 'OWE_BACK'
      ? direction === 'IN'
        ? -extraPaise
        : extraPaise
      : 0
  const netAfter = owedAll - clearedBy('OWED_TO_US') - (oweAll - clearedBy('WE_OWE')) + owedBack
  const afterNote =
    netAfter > 0
      ? `${name} will still owe you ${rupees(netAfter)}.`
      : netAfter < 0
        ? `You’ll still owe ${name} ${rupees(-netAfter)}.`
        : null

  const givenUp = sidePaise(tickedOpen, 'OWED_TO_US')
  const forgiven = sidePaise(tickedOpen, 'WE_OWE')

  const canSave =
    mode === 'PAID'
      ? paidAllocations.length > 0 && accountId !== ''
      : mode === 'EVEN'
        ? even.cancelledPaise > 0
        : tickedOpen.length > 0

  const mutation = useMutation({
    mutationFn: () =>
      createSettlement({
        contactId: contact.id,
        settledOn: date,
        financialAccountId: mode === 'PAID' ? accountId : undefined,
        allocations: allocationsPaise.map((a) => ({
          entryId: a.entryId,
          amount: fromPaise(a.amountPaise),
        })),
        ...(mode === 'WRITE_OFF' ? { writeOff: true } : {}),
        ...(mode === 'PAID' && payloadExtra ? { extra: payloadExtra } : {}),
      }),
    onSuccess: (data) => {
      for (const key of [
        'contacts',
        'owed-summary',
        'transactions',
        'accounts',
        'dashboard',
        'budget',
      ])
        queryClient.invalidateQueries({ queryKey: [key] })
      if (data?.earmarkWarning) {
        const goal = data.earmarkWarning.goals[0]
        toast.warning(`${goal.name} is now short by ${format(data.earmarkWarning.shortfall)}`, {
          action: { label: 'View goal', onClick: () => navigate(ROUTES.APP.GOAL_DETAIL(goal.id)) },
        })
      }
      onClose()
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setSubmitError(detail ?? 'Something went wrong. Please try again.')
    },
  })

  function toggle(id: string) {
    setAmountText(null)
    setTicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function pick(next: Mode) {
    setMode(next)
    setChoosingItems(false)
    setSubmitError(null)
  }

  const choices: [Mode, string, string, LucideIcon, string][] = [
    ...(overall !== 0
      ? [
          overall > 0
            ? ([
                'PAID',
                `${name} paid me`,
                'Money came into one of your accounts',
                ArrowDown,
                'primary',
              ] as const)
            : ([
                'PAID',
                `I paid ${name}`,
                'Money went out of one of your accounts',
                ArrowDown,
                'primary',
              ] as const),
        ]
      : []),
    ['WRITE_OFF', 'Write it off', 'No money changed hands — just clear it', MinusCircle, 'warning'],
    ...(owedAll > 0 && oweAll > 0
      ? [
          [
            'EVEN',
            'Call it even',
            `Cancel the ${rupees(even.cancelledPaise)} you owe each other`,
            ArrowLeftRight,
            'neutral',
          ] as [Mode, string, string, LucideIcon, string],
        ]
      : []),
  ].map((c) => [...c] as [Mode, string, string, LucideIcon, string])

  const title =
    mode === null ? `Settle up with ${name}` : (choices.find((c) => c[0] === mode)?.[1] ?? '')

  const itemsLink = (
    <div className="flex items-center justify-between rounded-xl bg-[var(--fl-surface-subtle)] px-3.5 py-3 text-[13px] text-[var(--fl-text-2)]">
      <span>
        {tickedOpen.length === entries.length
          ? `Covers all ${entries.length} open items`
          : `Covers ${tickedOpen.length} of ${entries.length} open items`}
      </span>
      <button
        type="button"
        onClick={() => setChoosingItems(true)}
        className="font-semibold text-[var(--fl-primary)] hover:underline"
      >
        Choose items
      </button>
    </div>
  )

  const datePicker = (
    <div>
      <span className={LABEL_CLS}>On</span>
      <DatePicker value={date} onChange={setDate} />
    </div>
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Settle up with ${name}`}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-[20px] bg-[var(--fl-surface)] px-4 pt-2 pb-[max(24px,env(safe-area-inset-bottom))] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] sm:mx-4 sm:my-8 sm:max-w-lg sm:rounded-[20px] sm:p-7"
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-2 h-1 w-10 rounded-full bg-[var(--fl-border)] sm:hidden"
        />
        <div className="flex items-center gap-1">
          {(mode !== null || choosingItems) && (
            <button
              type="button"
              aria-label="Back"
              onClick={() => (choosingItems ? setChoosingItems(false) : setMode(null))}
              className={cn(
                '-ml-2.5 grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]',
                !choosingItems && 'sm:hidden'
              )}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h2
            id={titleId}
            className="flex-1 font-serif text-[21px] font-semibold text-[var(--fl-heading)]"
          >
            {choosingItems ? (
              'Which items?'
            ) : (
              <>
                <span className="sm:hidden">{title}</span>
                <span className="hidden sm:inline">Settle up with {name}</span>
              </>
            )}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <X size={20} />
          </button>
        </div>

        {choosingItems ? (
          <ItemsPicker
            entries={entries}
            name={name}
            ticked={ticked}
            onToggle={toggle}
            summary={
              mode === 'PAID'
                ? direction === 'EVEN'
                  ? 'These cancel out'
                  : direction === 'IN'
                    ? `${name} pays ${rupees(netPaise)}`
                    : `You pay ${rupees(netPaise)}`
                : `${rupees(givenUp + forgiven)} cleared`
            }
            onDone={() => setChoosingItems(false)}
          />
        ) : (
          <>
            <p
              className={cn(
                'mt-0.5 mb-4 text-sm text-[var(--fl-text-2)]',
                mode !== null && 'hidden sm:block'
              )}
            >
              {overall > 0 ? (
                <>
                  {name} owes you{' '}
                  <strong className="text-[var(--fl-income)]">{rupees(overall)}</strong> overall
                </>
              ) : overall < 0 ? (
                <>
                  You owe {name}{' '}
                  <strong className="text-[var(--fl-expense)]">{rupees(-overall)}</strong> overall
                </>
              ) : (
                <>You owe each other the same amount</>
              )}
              {owedAll > 0 && oweAll > 0 && (
                <span className="text-[var(--fl-text-3)]">
                  {' '}
                  ({rupees(owedAll)} − {rupees(oweAll)})
                </span>
              )}
            </p>

            <p
              className={cn(
                SECTION_CLS,
                'mb-2 text-[var(--fl-text-2)]',
                mode !== null && 'hidden sm:block'
              )}
            >
              What happened?
            </p>
            <div
              role="radiogroup"
              aria-label="What happened?"
              className={cn(
                'flex flex-col gap-2.5 sm:grid sm:gap-2.5',
                choices.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
                mode !== null && 'hidden sm:mb-5 sm:grid'
              )}
            >
              {choices.map(([value, label, hint, Icon, tone]) => {
                const selected = mode === value
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => pick(value)}
                    className={cn(
                      'flex items-center gap-3.5 rounded-[14px] border p-4 text-left sm:flex-col sm:items-start sm:gap-1 sm:p-3.5',
                      selected
                        ? 'border-[1.5px] border-[var(--fl-primary)] bg-[var(--fl-primary-faint)]'
                        : 'border-[var(--fl-border)] hover:bg-[var(--fl-surface-hover)]'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'grid h-11 w-11 shrink-0 place-items-center rounded-xl sm:hidden',
                        tone === 'primary' && 'bg-[var(--fl-primary)] text-[var(--fl-primary-fg)]',
                        tone === 'warning' && 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]',
                        tone === 'neutral' &&
                          'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
                      )}
                    >
                      <Icon size={22} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold text-[var(--fl-heading)]">{label}</span>
                      <span className="block text-[13px] text-[var(--fl-text-2)] sm:text-xs">
                        {hint}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
            {mode === null && owedAll > 0 && oweAll > 0 && (
              <p className="mt-3 text-xs text-[var(--fl-text-3)] sm:hidden">
                Loans aren’t part of Settle up — open a loan to repay or write it off.
              </p>
            )}

            {mode === 'PAID' && (
              <div className="flex flex-col gap-4">
                <div>
                  <label htmlFor="settle-amount" className={LABEL_CLS}>
                    How much?
                  </label>
                  <div className="flex h-14 items-center gap-1.5 rounded-xl border-[1.5px] border-[var(--fl-primary)] px-3.5 focus-within:ring-2 focus-within:ring-[var(--fl-primary)]/20">
                    <span className="font-serif text-2xl text-[var(--fl-text-2)]">₹</span>
                    <input
                      id="settle-amount"
                      inputMode="decimal"
                      value={amountText ?? String(fromPaise(netPaise))}
                      onChange={(e) => setAmountText(e.target.value)}
                      className="w-full bg-transparent font-serif text-[26px] font-semibold text-[var(--fl-heading)] outline-none"
                    />
                  </div>
                  {amountError ? (
                    <p className="mt-1.5 text-xs text-[var(--fl-error)]">{amountError}</p>
                  ) : extraPaise > 0 ? null : (
                    <p className="mt-1.5 text-xs text-[var(--fl-text-2)]">
                      {amountText === null
                        ? `The full balance. Change it if ${direction === 'OUT' ? 'you' : name} paid part of it.`
                        : 'The oldest items are cleared first.'}
                    </p>
                  )}
                </div>
                {extraPaise > 0 && (
                  <ExtraChoices
                    prompt={
                      direction === 'OUT'
                        ? `You’re paying ${rupees(extraPaise)} more than you owe for these items. What about the extra?`
                        : `That’s ${rupees(extraPaise)} more than ${name} owes for these items. What about the extra?`
                    }
                    value={extraHandling}
                    onChange={setExtraChoice}
                    options={[
                      ...(canUseOtherItems
                        ? [
                            {
                              value: 'OTHER_ITEMS' as const,
                              label:
                                direction === 'OUT'
                                  ? `Take it off what else I owe ${name}`
                                  : `Take it off what else ${name} owes me`,
                              hint: 'Clears their other open items, oldest first',
                            },
                          ]
                        : []),
                      {
                        value: 'OWE_BACK' as const,
                        label:
                          direction === 'OUT'
                            ? `${name} owes me ${rupees(extraPaise)}`
                            : `I owe ${name} ${rupees(extraPaise)}`,
                        hint:
                          direction === 'OUT'
                            ? `Added to what ${name} owes you`
                            : `Added to what you owe ${name}`,
                      },
                      {
                        value: 'ROUNDED_OFF' as const,
                        label: direction === 'OUT' ? 'Let them keep it' : 'I’ll keep it',
                        hint:
                          direction === 'OUT'
                            ? 'Saved as spending under Rounded off'
                            : 'Saved as income under Rounded off',
                      },
                    ]}
                  />
                )}
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <span className={LABEL_CLS}>{direction === 'OUT' ? 'From' : 'Into'}</span>
                    <Select
                      aria-label={direction === 'OUT' ? 'From' : 'Into'}
                      value={accountId}
                      onChange={setAccountId}
                    >
                      <option value="">Select account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {datePicker}
                </div>
                {itemsLink}
                {direction === 'EVEN' ? (
                  <Note tone="neutral">These items cancel out — use Call it even instead.</Note>
                ) : (
                  !amountError &&
                  (extraPaise === 0 || extraHandling) && (
                    <Note tone="good">
                      {afterNote ?? (
                        <>
                          After this you’re <strong>all settled</strong> with {name}.
                        </>
                      )}
                      {extraHandling === 'ROUNDED_OFF' &&
                        ` The extra ${rupees(extraPaise)} is saved under Rounded off.`}
                    </Note>
                  )
                )}
              </div>
            )}

            {mode === 'WRITE_OFF' && (
              <div className="flex flex-col gap-4">
                <p className="-mt-1 text-[13px] text-[var(--fl-text-2)] sm:mt-0">
                  Use this when nobody is going to pay — a gift, a favour, or money you’ve given up
                  on.
                </p>
                <ul
                  aria-label="What gets cleared"
                  className="overflow-hidden rounded-[14px] border border-[var(--fl-border)]"
                >
                  {givenUp > 0 && (
                    <li className="flex items-center justify-between border-b border-[var(--fl-divider)] px-4 py-3.5 text-sm">
                      <span>You give up what {name} owes you</span>
                      <span className="font-semibold text-[var(--fl-income)]">
                        {rupees(givenUp)}
                      </span>
                    </li>
                  )}
                  {forgiven > 0 && (
                    <li className="flex items-center justify-between border-b border-[var(--fl-divider)] px-4 py-3.5 text-sm">
                      <span>{name} lets go of what you owe</span>
                      <span className="font-semibold text-[var(--fl-expense)]">
                        {rupees(forgiven)}
                      </span>
                    </li>
                  )}
                  <li className="flex items-center justify-between bg-[var(--fl-surface-hover)] px-4 py-3.5 text-sm font-semibold">
                    <span>
                      {givenUp - forgiven >= 0
                        ? 'Your net worth goes down by'
                        : 'Your net worth goes up by'}
                    </span>
                    <span className="font-serif text-xl">
                      {rupees(Math.abs(givenUp - forgiven))}
                    </span>
                  </li>
                </ul>
                <Note tone="warning">
                  No account changes. It isn’t counted as spending or income. You can undo it from
                  History.
                </Note>
                {datePicker}
                {itemsLink}
              </div>
            )}

            {mode === 'EVEN' && (
              <div className="flex flex-col gap-4">
                <p className="-mt-1 text-[13px] text-[var(--fl-text-2)] sm:mt-0">
                  You owe each other money. Cancel the matching amount so only the difference is
                  left.
                </p>
                <div className="flex items-center justify-between rounded-xl bg-[var(--fl-surface-subtle)] px-4 py-3.5">
                  <span className="text-sm">Cancelled each way</span>
                  <span className="font-serif text-xl font-semibold">
                    {rupees(even.cancelledPaise)}
                  </span>
                </div>
                <p className="text-[13px] text-[var(--fl-text-2)]">
                  No money moves and no account changes.{' '}
                  {afterNote
                    ? `${afterNote.replace(/\.$/, '')} — settle that later.`
                    : `You’ll be all settled with ${name}.`}
                </p>
                {datePicker}
              </div>
            )}

            {submitError && <p className="mt-3 text-xs text-[var(--fl-error)]">{submitError}</p>}

            {mode !== null && (
              <div className="mt-5 flex gap-2.5 sm:justify-end">
                <Button variant="outline" onClick={onClose} className="hidden h-11 sm:inline-flex">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setSubmitError(null)
                    mutation.mutate()
                  }}
                  disabled={!canSave || mutation.isPending}
                  className={cn(
                    'h-[52px] flex-1 rounded-xl text-base font-semibold sm:h-11 sm:flex-none sm:px-5',
                    mode === 'WRITE_OFF' &&
                      'bg-[var(--fl-warning)] text-[var(--fl-warning-bg)] hover:bg-[var(--fl-warning-dark)]'
                  )}
                >
                  {mutation.isPending
                    ? 'Saving…'
                    : mode === 'PAID'
                      ? `Save · ${amountError ? '' : rupees(paidPaise)} ${direction === 'OUT' ? 'paid' : 'received'}`
                      : mode === 'EVEN'
                        ? `Cancel ${rupees(even.cancelledPaise)} each way`
                        : 'Write off'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ExtraChoices({
  prompt,
  value,
  onChange,
  options,
}: {
  prompt: string
  value: ExtraChoice | null
  onChange: (v: ExtraChoice) => void
  options: { value: ExtraChoice; label: string; hint: string }[]
}) {
  return (
    <div>
      <p className="mb-2 text-[13px] text-[var(--fl-text-1)]">{prompt}</p>
      <div
        role="radiogroup"
        aria-label="What about the extra?"
        className="overflow-hidden rounded-[14px] border border-[var(--fl-border)]"
      >
        {options.map((o) => {
          const selected = value === o.value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(o.value)}
              className={cn(
                'flex min-h-[52px] w-full items-center gap-3 border-b border-[var(--fl-divider)] px-3.5 py-2.5 text-left last:border-b-0',
                selected ? 'bg-[var(--fl-primary-faint)]' : 'hover:bg-[var(--fl-surface-hover)]'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-[1.5px]',
                  selected ? 'border-[var(--fl-primary)]' : 'border-[var(--fl-border)]'
                )}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-[var(--fl-primary)]" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[var(--fl-heading)]">
                  {o.label}
                </span>
                <span className="block text-xs text-[var(--fl-text-2)]">{o.hint}</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Note({
  tone,
  children,
}: {
  tone: 'good' | 'warning' | 'neutral'
  children: React.ReactNode
}) {
  const Icon = tone === 'good' ? Check : AlertCircle
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-[13px]',
        tone === 'good' && 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]',
        tone === 'warning' && 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]',
        tone === 'neutral' && 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
      )}
    >
      <Icon size={17} aria-hidden="true" className="mt-px shrink-0" />
      <span>{children}</span>
    </div>
  )
}

function ItemsPicker({
  entries,
  name,
  ticked,
  onToggle,
  summary,
  onDone,
}: {
  entries: OwedEntry[]
  name: string
  ticked: Set<string>
  onToggle: (id: string) => void
  summary: string
  onDone: () => void
}) {
  const { format } = useFormatCurrency()
  const group = (dir: OwedEntry['direction'], label: string, color: string) => {
    const list = entries.filter((e) => e.direction === dir)
    if (list.length === 0) return null
    return (
      <div className="mb-3.5">
        <p className={cn(SECTION_CLS, 'mb-1.5', color)}>{label}</p>
        <ul className="overflow-hidden rounded-xl border border-[var(--fl-border)]">
          {list.map((e) => (
            <li key={e.id} className="border-b border-[var(--fl-divider)] last:border-b-0">
              <label className="flex min-h-[52px] cursor-pointer items-center gap-3 px-3.5 py-2">
                <Checkbox checked={ticked.has(e.id)} onChange={() => onToggle(e.id)} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-[var(--fl-text-1)]">
                    {e.description}
                  </span>
                  <span className="block text-xs text-[var(--fl-text-3)]">
                    {formatShortDate(e.entryDate)}
                    {e.outstanding !== e.amount &&
                      ` · ${format(e.outstanding)} left of ${format(e.amount)}`}
                  </span>
                </span>
                <span
                  className={cn(
                    'text-sm font-semibold tabular-nums',
                    !ticked.has(e.id) && 'text-[var(--fl-text-3)]'
                  )}
                >
                  {format(e.outstanding)}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    )
  }
  const count = entries.filter((e) => ticked.has(e.id)).length
  return (
    <div className="mt-1">
      <p className="mb-3 text-[13px] text-[var(--fl-text-2)]">
        Untick anything this isn’t for. Usually you can leave them all ticked.
      </p>
      {group('OWED_TO_US', `${name} owes you`, 'text-[var(--fl-income)]')}
      {group('WE_OWE', `You owe ${name}`, 'text-[var(--fl-expense)]')}
      <div className="mb-3.5 flex items-baseline justify-between rounded-xl bg-[var(--fl-surface-subtle)] px-3.5 py-3">
        <span className="text-[13px] text-[var(--fl-text-2)]">
          {count} of {entries.length} ticked
        </span>
        <span className="font-semibold text-[var(--fl-heading)]">{summary}</span>
      </div>
      <Button
        onClick={onDone}
        className="h-[52px] w-full rounded-xl text-base font-semibold sm:h-11"
      >
        Done
      </Button>
    </div>
  )
}
